import { useAuthStore } from '@/auth/authStore';
import { COMMON_HEADERS, SERVER_URL } from '@/config/serverUrl';
import type { ColorType, RankTier } from '@shared/types';

export interface ChallengePlayer {
  id: string;
  name: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  mmr: number;
  rank: RankTier;
}

export interface FriendChallengeDto {
  code: string;
  status: 'open' | 'active' | 'completed' | 'expired' | 'cancelled';
  creator: ChallengePlayer;
  target: ChallengePlayer | null;
  targetDurationMs: number;
  targetAccuracy: number;
  targetMaxCombo: number;
  steps: number;
  expiresAt: string;
  createdAt: string;
  mine: boolean;
  canPlay: boolean;
  attempt: {
    steps: number;
    durationMs: number | null;
    accuracy: number;
    maxCombo: number;
    won: boolean | null;
    finishedAt: string | null;
  } | null;
}

export interface FriendChallengeRun {
  code: string;
  ticket: string;
  seed: number;
  steps: number;
  targetDurationMs: number;
  ghostTimes: number[];
  creator: ChallengePlayer;
}

export interface FriendChallengeResult {
  code: string;
  won: boolean;
  steps: number;
  durationMs: number;
  accuracy: number;
  maxCombo: number;
  targetDurationMs: number;
}

export interface ChallengeTapEvent {
  color: ColorType;
  t: number;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const response = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    let code = `HTTP_${response.status}`;
    try { code = ((await response.json()) as { error?: string }).error ?? code; } catch { /* noop */ }
    throw new Error(code);
  }
  return response.json() as Promise<T>;
}

export const challengeApi = {
  list: () => request<{ challenges: FriendChallengeDto[] }>('GET', '/friend-challenges'),
  get: (code: string) => request<{ challenge: FriendChallengeDto }>('GET', `/friend-challenges/${encodeURIComponent(code)}`),
  create: (input: { matchId?: string; targetUserId?: string } = {}) =>
    request<{ challenge: FriendChallengeDto }>('POST', '/friend-challenges', input),
  begin: (code: string) => request<{ run: FriendChallengeRun }>('POST', `/friend-challenges/${encodeURIComponent(code)}/begin`),
  finish: (code: string, ticket: string, events: ChallengeTapEvent[]) =>
    request<{ result: FriendChallengeResult }>('POST', `/friend-challenges/${encodeURIComponent(code)}/finish`, { ticket, events }),
  rematch: (code: string) => request<{ challenge: FriendChallengeDto }>('POST', `/friend-challenges/${encodeURIComponent(code)}/rematch`),
};

export const challengeShareUrl = (code: string): string =>
  `https://dontmiss.games/challenge?code=${encodeURIComponent(code)}`;
